import React, { type FC, memo, useEffect, useRef } from "react";
import { load, unload, type WebFormProps } from "@finapi/web-form";
import { cn } from "@heroui/react";
import { type GQL } from "@binale-tech/shared";

type Props = {
    webForm?: GQL.IFinApiWebFormResponse;
    isComplete: boolean;
    onComplete: () => void;
    onError: (error: string) => void;
};

const FinApiWebFormEmbedImpl: FC<Props> = ({ webForm, isComplete, onComplete, onError }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    // Callbacks go through a ref so a re-rendered parent (e.g. the bank's RTDB row changed) does not reload the form
    const callbacksRef = useRef({ onComplete, onError });
    callbacksRef.current = { onComplete, onError };

    useEffect(() => {
        if (!containerRef.current || !webForm?.id) {
            return undefined;
        }

        const props: WebFormProps = {
            targetUrl: webForm.url.split("/wf/")[0],
            token: webForm.id,
        };

        load(containerRef.current, props, {
            onComplete: () => {
                unload();
                callbacksRef.current.onComplete();
            },
            onFail: () => {
                unload();
                callbacksRef.current.onError("Web Form failed");
            },
            onLoadError: () => {
                unload();
                callbacksRef.current.onError("Web Form failed to load");
            },
            onAbort: () => {
                unload();
            },
        });

        return () => {
            unload();
        };
    }, [webForm]);

    return (
        <div
            ref={containerRef}
            className={cn("min-h-[50px] w-full", {
                "min-h-px": !webForm || isComplete,
            })}
        />
    );
};

export const FinApiWebFormEmbed = memo(FinApiWebFormEmbedImpl);
